import { dxtToLocalServerTime } from "@dexteel/mesf-core";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import { IconButton, Stack } from "@mui/material";
import type { ColDef, ICellRendererParams } from "ag-grid-community";
import type { Inspection } from "../../models/Inspection";
import { SectionCell } from "../../report/hooks/useTableData";

type Props = {
	openInspection: (inspection: Inspection) => void;
	deleteInspection: (inspection: Inspection) => void;
};

export const useTableData = ({ openInspection, deleteInspection }: Props) => {
	const columnDefs: ColDef[] = [
		{
			field: "SectionName",
			headerName: "Section",
			cellRenderer: "agGroupCellRenderer",
			cellRendererParams: { innerRenderer: SectionCell },
			flex: 2,
			minWidth: 160,
		},
		{
			field: "Rev",
			headerName: "Revision",
			valueFormatter: (params) => `Rev ${params.value}`,
			flex: 1,
			minWidth: 110,
		},
		{
			field: "InspectorName",
			headerName: "Inspector",
			flex: 2,
			minWidth: 140,
			valueFormatter: (params) => params.value ?? "-",
		},
		{
			field: "RowCountTotal",
			headerName: "Readings",
			flex: 1,
			minWidth: 100,
			type: "rightAligned",
		},
		{
			field: "LastRowTimestamp",
			headerName: "Last Reading",
			flex: 1,
			minWidth: 130,
			valueFormatter: (params) => dxtToLocalServerTime(params.value, "HH:mm"),
		},
		{
			field: "Comments",
			headerName: "Comments",
			flex: 2,
			minWidth: 160,
		},
		{
			headerName: "",
			colId: "actions",
			pinned: "right",
			sortable: false,
			filter: false,
			resizable: false,
			maxWidth: 110,
			minWidth: 110,
			cellRenderer: (params: ICellRendererParams<Inspection>) =>
				params.data ? (
					<Stack
						direction="row"
						spacing={0.5}
						justifyContent="center"
						alignItems="center"
						height="100%"
					>
						<IconButton
							size="small"
							color="primary"
							title="Open sheet"
							onClick={() => openInspection(params.data as Inspection)}
						>
							<OpenInNewIcon fontSize="small" />
						</IconButton>
						<span
							title={
								params.data.IsEditable ? "Delete sheet" : "This sheet is closed"
							}
						>
							<IconButton
								size="small"
								color="error"
								disabled={!params.data.IsEditable}
								onClick={() => deleteInspection(params.data as Inspection)}
							>
								<DeleteOutlineIcon fontSize="small" />
							</IconButton>
						</span>
					</Stack>
				) : null,
		},
	];

	return { columnDefs };
};
